import React, { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import { getNodes } from "./selectors";
import { ALLOWED_PARENT_TYPES } from "./flow.hooks";
import { Node } from "reactflow";
import api from "core/api/api";
import MetaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import lastConnectionTestRepository from "app/components/common/editor/control/connection-text-field/last-connection-test-repository";
import dialog from "app/components/common/dialogs/dialogWindow";
import growl from "app/components/common/growl";
import App from "app";
import utils from "core/utils";
import { getComponentId, getNodeType, shouldExcludeComponentType } from "./component.utils";
import { LAYOUT_CONFIG } from "./flow.constants";
import nameFormatter from "app/components/common/editor/name-formatter";
import { Dispatch } from "@reduxjs/toolkit";
import {
    setNodesAction,
    setSelectedNodeAction,
    setIsRightSidebarOpenAction,
    deleteNodeAction,
    closeRightSidebarAction
} from "../../../../stores/redux/features/flowdesigner/flowdesigner.slice";

// ============================================================================
// NODE CREATION UTILITIES
// ============================================================================

/**
 * Creates a new node from drag and drop data
 */
export const createNodeFromDrop = (
    type: string,
    name: string,
    componentId: string,
    position: { x: number; y: number },
    nodeIdCounter: number
): Node => {
    return {
        id: `node-${nodeIdCounter}`,
        type: type,
        position,
        data: {
            label: name,
            type: type,
            componentType: type,
            name: name,
            componentId: componentId,
            // This will be used by the Sidebar to create a new metaObject
            metaObjectId: "temp-node",
            targets: [],
            metaObject: { type: type, name: name }
        }
    };
};

/**
 * Creates a component node from application component data
 */
export const createComponentNode = (component: any, position: { x: number; y: number }): Node => {
    const componentType = component.type;
    const nodeType = getNodeType(componentType);
    const componentName = component.name || MetaObjectConverter.getName(component.id);
    const compId = getComponentId(component, nodeType);

    return {
        id: component.id,
        type: nodeType,
        position: position,
        data: {
            label: componentName,
            type: nodeType,
            componentType: componentType,
            name: componentName,
            componentId: compId,
            metaObjectId: component.id,
            targets: [],
            metaObject: component
        }
    };
};

/**
 * Creates a new node from copied metadata (Copy to app functionality)
 * This replicates the behavior of the old Flow Designer's copy-to-app feature
 */
export const createNodeFromMetadata = (copiedItem: any, nodeIdCounter: number): Node => {
    // Generate unique IDs
    const nodeId = `node-${nodeIdCounter}`;
    const position = { x: 0, y: 0 };
    const tempComponentId = `temp-${copiedItem.type.toLowerCase()}-${Date.now()}`;

    // Determine which properties to copy based on component type (matches cloneMetaObjectProcessor.js)
    let properties: string[] = [];
    switch (copiedItem.type) {
        case utils.ENTITIES.CACHE:
            properties = ["adapter", "parser", "queryProperties", "typename", "isEventTable"];
            break;
        case utils.ENTITIES.SOURCE:
            properties = ["adapter", "parser"];
            break;
        case utils.ENTITIES.TARGET:
            properties = ["adapter", "formatter"];
            break;
        default:
            properties = [];
    }

    // Pick only the relevant properties from copiedItem
    const autoProps: any = {};
    properties.forEach((prop: string) => {
        if (copiedItem[prop]) {
            autoProps[prop] = copiedItem[prop];
        }
    });

    // Clear passwords like the old Flow Designer (clearPasswordsProcessor.js)
    Object.keys(autoProps).forEach(autoPropertyKey => {
        const autoProperty = autoProps[autoPropertyKey];
        if (autoProperty && autoProperty.properties) {
            Object.keys(autoProperty.properties).forEach(propertyKey => {
                const property = autoProperty.properties[propertyKey];
                // If property contains "encrypted" field it means this is password and needs to be cleared
                if (property && property.encrypted) {
                    autoProperty.properties[propertyKey] = null;
                }
            });
        }
    });

    return {
        id: nodeId,
        type: copiedItem.type,
        position,
        data: {
            label: nameFormatter.format(copiedItem.type),
            type: copiedItem.type,
            componentType: copiedItem.type,
            name: nameFormatter.format(copiedItem.type),
            componentId: tempComponentId,
            metaObjectId: null,
            targets: [],
            clonedProperties: autoProps,
            metaObject: {
                type: copiedItem.type
            }
        }
    };
};

/**
 * Calculates the next position for a node in a grid layout
 */
export const calculateNodePosition = (
    componentsInRow: number,
    xOffset: number,
    yOffset: number
): { position: { x: number; y: number }; newXOffset: number; newYOffset: number; newComponentsInRow: number } => {
    let newXOffset = xOffset;
    let newYOffset = yOffset;
    let newComponentsInRow = componentsInRow;

    if (componentsInRow >= LAYOUT_CONFIG.MAX_COMPONENTS_PER_ROW) {
        newComponentsInRow = 0;
        newYOffset += LAYOUT_CONFIG.VERTICAL_SPACING;
        newXOffset = LAYOUT_CONFIG.INITIAL_X_OFFSET;
    }

    const position = {
        x: newXOffset,
        y: newYOffset
    };

    newXOffset += LAYOUT_CONFIG.HORIZONTAL_SPACING;
    newComponentsInRow++;

    return { position, newXOffset, newYOffset, newComponentsInRow };
};

// ============================================================================
// COPY TO APP UTILITIES
// ============================================================================

/**
 * Handles the copy-to-app functionality from the metadata browser
 * This function creates a new node from copied metadata and updates the Redux state
 */
export const handleCopyToApp = (id: string, nodes: Node[], dispatch: Dispatch<any>) => {
    metaStoreService.findById(id).then((copiedItem: any) => {
        if (!copiedItem) return;

        // Use the utility function to create the node from copied metadata
        const nodeIdCounter = nodes.length + 1;
        const newNode = createNodeFromMetadata(copiedItem, nodeIdCounter);

        // Position the new node below/beside existing nodes without moving them
        if (nodes.length > 0) {
            const maxY = Math.max(...nodes.map(n => n.position.y));
            newNode.position = {
                x: LAYOUT_CONFIG.INITIAL_X_OFFSET,
                y: maxY + LAYOUT_CONFIG.VERTICAL_SPACING + LAYOUT_CONFIG.INITIAL_Y_OFFSET
            };
        }

        // Add the new node without re-layouting existing nodes
        const updatedNodes = [...nodes, newNode];

        dispatch(setNodesAction(updatedNodes));
        dispatch(setSelectedNodeAction(newNode));
        dispatch(setIsRightSidebarOpenAction(true));

        // Close metadata browser
        App.vent.trigger("metadata-browser:close");
    });
};

// ============================================================================
// NODE FILTERING UTILITIES
// ============================================================================

/**
 * Determines whether a component should be included in the current canvas view.
 *
 * Skip PROPERTYSET and DASHBOARD Components
 * if inside a flow
 *      Skip components that are not part if current flow
 * else if in App view
 *      Skip components that are components that are inside flows
 */
export const shouldIncludeComponent = (
    component: any,
    isViewingSpecificFlow: boolean,
    componentsInSelectedFlow: Set<string>,
    componentsInFlows: Set<string>,
    componentToFlowMap?: Map<string, string>,
    appFQN?: string
): boolean => {
    const componentType = component.type;

    // Skip excluded component types
    if (shouldExcludeComponentType(componentType)) {
        return false;
    }

    // Filter logic based on whether we're viewing a specific flow or main app
    if (isViewingSpecificFlow) {
        // When viewing a specific flow, only show components that belong to that flow
        return componentsInSelectedFlow.has(component.id);
    } else {
        // When viewing the main app, skip components that are inside flows (except for the flow itself)
        // OR
        // Component is in the app level
        return (
            componentType === "FLOW" ||
            !componentsInFlows.has(component.id) ||
            (componentToFlowMap && componentToFlowMap.get(component.id) === appFQN)
        );
    }
};

// ============================================================================
// NODE DELETION UTILITIES
// ============================================================================

/**
 * Deletes a node from the flow with confirmation dialog and backend API calls
 * This matches the behavior of the old flow designer
 *
 * @param selectedNode - The node to delete
 * @param appModel - The application model containing app name
 * @param fetchAndPopulateNodes - Function to reload flow data after deletion
 * @returns Promise that resolves with deletion result or null if cancelled
 */
export interface DeleteNodeOptions {
    /** Skip the per-node confirmation dialog (caller is responsible for any prompt). */
    skipConfirm?: boolean;
    /** Skip the post-delete app recompile + refetch (caller will do it once after a batch). */
    skipRecompileAndFetch?: boolean;
    /** Suppress the per-node success growl (caller will show a single summary growl). */
    skipGrowl?: boolean;
}

export const deleteNode = async (
    selectedNode: Node | null,
    appModel: any,
    fetchAndPopulateNodes: () => Promise<void>,
    options: DeleteNodeOptions = {}
): Promise<{ success: boolean; nodeId: string } | null> => {
    if (!selectedNode) return null;

    const metaObjectId = selectedNode.data?.metaObjectId;
    const isNewNode = metaObjectId === "temp-node" || !metaObjectId;
    const nodeName = selectedNode.data?.label || selectedNode.data?.name || "this component";

    if (!options.skipConfirm) {
        const message = `Are you sure you want to drop ${nodeName}?`;
        const confirmed = await dialog.confirm(
            message,
            App.Dashboards.ModalManager,
            "delete-component deleteModal",
            null,
            "Delete"
        );

        if (!confirmed) {
            return null;
        }
    }

    try {
        // If it's a saved node (not a temp node), delete it from the backend
        if (!isNewNode) {
            // Fetch the metaObject from the backend
            const metaObject = await metaStoreService.findById(metaObjectId);

            if (metaObject) {
                const nodeType = metaObject.get("type");
                const isStream = nodeType === utils.ENTITIES.STREAM;

                // For SOURCE nodes, clear the last connection test result
                if (nodeType === utils.ENTITIES.SOURCE && metaObject.get("adapter")?.handler) {
                    await lastConnectionTestRepository.save(metaObjectId, metaObject.get("adapter").handler, null);
                }

                // Delete streams first, then other components
                // This matches the order in the old flow designer
                if (isStream || nodeType !== utils.ENTITIES.STREAM) {
                    await metaObject.destroy();
                }

                if (!options.skipRecompileAndFetch) {
                    // Recompile the app after deletion
                    await api.compile(appModel.name);
                }
            }
        }

        if (!options.skipGrowl) {
            growl.success(`Component ${nodeName} deleted successfully`, "Success", undefined, appModel?.name);
        }
        return { success: true, nodeId: selectedNode.id };
    } catch (error) {
        growl.error(`Component drop failed! ${error?.message || error}`, "Error", undefined, appModel?.name);
        console.error("Delete node error:", error);
        throw error;
    } finally {
        if (!options.skipRecompileAndFetch) {
            // Reload the flow data to ensure UI is in sync with backend
            await fetchAndPopulateNodes();
        }
    }
};

/**
 * Deletes multiple nodes in a single batch: one confirmation prompt, one recompile,
 * and one refetch for the whole selection (instead of per-node).
 */
export interface DeleteMultipleNodesParams {
    selectedNodes: Node[];
    appModel: any;
    fetchAndPopulateNodes: (options?: { skipReCenter?: boolean }) => Promise<void>;
    dispatch: Dispatch<any>;
    appName?: string;
    flowName?: string;
}

export const deleteMultipleNodes = async ({
    selectedNodes,
    appModel,
    fetchAndPopulateNodes,
    dispatch,
    appName,
    flowName
}: DeleteMultipleNodesParams): Promise<void> => {
    if (selectedNodes.length === 0) return;

    const appNameValue = appModel?.name ?? appModel?.get?.("name");
    const count = selectedNodes.length;
    const confirmed = await dialog.confirm(
        `Are you sure you want to drop ${count} selected components?`,
        App.Dashboards.ModalManager,
        "delete-component deleteModal",
        null,
        "Delete"
    );
    if (!confirmed) return;

    try {
        const results = await Promise.all(
            selectedNodes.map(node =>
                deleteNode(node, appModel, fetchAndPopulateNodes, {
                    skipConfirm: true,
                    skipRecompileAndFetch: true,
                    skipGrowl: true
                })
            )
        );
        const deletedIds = results.filter((r): r is { success: boolean; nodeId: string } => !!r).map(r => r.nodeId);
        deletedIds.forEach(id => dispatch(deleteNodeAction(id)));

        // One recompile + one refetch for the whole batch.
        const anyPersisted = selectedNodes.some(n => n.data?.metaObjectId && n.data?.metaObjectId !== "temp-node");
        if (anyPersisted && appNameValue) {
            try {
                await api.compile(appNameValue);
            } catch (error) {
                console.error("Recompile after multi-delete failed:", error);
            }
            await fetchAndPopulateNodes({ skipReCenter: true });
        }

        dispatch(closeRightSidebarAction({ appName, flowName }));
        growl.success(`${deletedIds.length} components deleted successfully`, "Success", undefined, appNameValue);
    } catch (error) {
        console.error("Multi-delete error:", error);
    }
};

export const isTempNode = data => data.metaObjectId === "temp-node";

export const findNode = (nodes, name) => nodes.find(node => node?.data?.label === name);

export const isDuplicatable = (selectedNode, appStatus) => {
    return (
        selectedNode === null ||
        ![utils.ENTITIES.EXTERNALSOURCE, utils.ENTITIES.SOURCE, utils.ENTITIES.TARGET, utils.ENTITIES.CACHE].includes(
            selectedNode?.data?.componentType
        ) ||
        appStatus !== "CREATED"
    );
};

export const getClonedNode = (sourceNode: Node, newId: string, isCut: boolean): Node => {
    const sourceMetaObject = sourceNode.data?.metaObject || {};
    const nodeType = sourceNode.data?.type;

    let propertiesToClone: string[] = [];
    switch (nodeType) {
        case utils.ENTITIES.CACHE:
            propertiesToClone = ["adapter", "parser", "queryProperties", "typename", "isEventTable"];
            break;
        case utils.ENTITIES.SOURCE:
            propertiesToClone = ["adapter", "parser"];
            break;
        case utils.ENTITIES.TARGET:
            propertiesToClone = ["adapter", "formatter"];
            break;
    }

    const clonedProperties = propertiesToClone.reduce((acc, key) => {
        if (sourceMetaObject[key] !== undefined) {
            acc[key] = sourceMetaObject[key];
        }
        return acc;
    }, {} as Record<string, any>);

    const defaultName = isCut ? sourceNode.data?.name : `New ${nameFormatter.format(nodeType)}`;

    return {
        ...sourceNode,
        id: newId,
        position: { x: sourceNode.position.x + 300, y: sourceNode.position.y + 50 },
        data: {
            ...sourceNode.data,
            name: defaultName,
            label: defaultName,
            metaObjectId: isCut ? defaultName : "temp-node",
            metaObject: { type: sourceNode.data?.type },
            clonedProperties: Object.keys(clonedProperties).length > 0 ? clonedProperties : null
        }
    };
};

// ============================================================================
// DRAG INTERACTION HOOK
// ============================================================================

/**
 * Manages visual feedback during left-sidebar drag operations:
 * - Dims nodes that are not valid drop targets for the dragged component type
 * - Highlights the node currently under the cursor (if it is a valid target)
 * - Cleans up on drop or drag-cancel
 */
export const useNodeDragInteraction = (onDragOver: (event: React.DragEvent<HTMLDivElement>) => void) => {
    const dispatch = useAppDispatch();
    const nodes = useAppSelector(getNodes);
    const nodesRef = useRef(nodes);
    useEffect(() => {
        nodesRef.current = nodes;
    }, [nodes]);

    const dragHighlightRef = useRef<Element | null>(null);

    const clearDropHighlight = useCallback(() => {
        dragHighlightRef.current?.classList.remove("fd-drop-target-valid");
        dragHighlightRef.current = null;
    }, []);

    const applyDimming = useCallback(
        (draggedType: string) => {
            const allowedParents = ALLOWED_PARENT_TYPES[draggedType] || [];
            dispatch(
                setNodesAction(
                    nodesRef.current.map(node => {
                        const nodeType = node.data?.componentType;
                        const isDimmed = !nodeType || !allowedParents.includes(nodeType);
                        return { ...node, data: { ...node.data, isDimmed } };
                    })
                )
            );
        },
        [dispatch]
    );

    const clearDimming = useCallback(() => {
        dispatch(
            setNodesAction(
                nodesRef.current.map(node => ({
                    ...node,
                    data: { ...node.data, isDimmed: false }
                }))
            )
        );
    }, [dispatch]);

    // Dim unsupported nodes on drag start; restore on drag stop
    useEffect(() => {
        const onDragStart = ({ type }: { type: string }) => applyDimming(type);
        const onDragStop = () => {
            clearDropHighlight();
            clearDimming();
        };
        App.vent.on("fd:ls:on-drag-start", onDragStart);
        App.vent.on("fd:ls:on-drag-stop", onDragStop);
        return () => {
            App.vent.off("fd:ls:on-drag-start", onDragStart);
            App.vent.off("fd:ls:on-drag-stop", onDragStop);
        };
    }, [applyDimming, clearDimming, clearDropHighlight]);

    const handleDragOver = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            onDragOver(event);
            const elements = document.elementsFromPoint(event.clientX, event.clientY);
            let foundNode: Element | null = null;
            for (const el of elements) {
                const nodeEl = el.closest(".react-flow__node");
                if (nodeEl) {
                    foundNode = nodeEl;
                    break;
                }
            }
            if (dragHighlightRef.current !== foundNode) {
                clearDropHighlight();
                dragHighlightRef.current = foundNode;
                if (foundNode) {
                    const nodeId = (foundNode as HTMLElement).getAttribute("data-id");
                    const node = nodesRef.current.find(n => n.id === nodeId);
                    if (!node?.data?.isDimmed) {
                        foundNode.classList.add("fd-drop-target-valid");
                    }
                }
            }
        },
        [onDragOver, clearDropHighlight]
    );

    return { handleDragOver, clearDropHighlight, clearDimming };
};

export const setSavedSelection = (appName: string | undefined, nodeName: string | undefined) => {
    if (!appName || !nodeName) return;
    sessionStorage.setItem(`striim-selected-node-${appName}`, nodeName);
};

export const getSavedSelection = (appName: string) => {
    if (!appName) return;
    return sessionStorage.getItem(`striim-selected-node-${appName}`);
};

export const removeSavedSelection = (appName: string | undefined) => {
    if (!appName) return;
    sessionStorage.removeItem(`striim-selected-node-${appName}`);
};
